// MiniBarItemMaster.js  Master CRUD + low-stock warning.
import React, { useEffect, useState } from 'react';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Switch, FormControlLabel, Chip, Snackbar, Alert,
  CircularProgress, MenuItem, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import { miniBarApi, ctx } from './modulesApi';

const EMPTY = {
  miniBarItemId: 0, companyId: 0, hotelId: 0,
  productName: '', category: '', sku: '', hsnCode: '', taxType: 'GST',
  price: 0, gstPercent: 18, stockQuantity: 0, reorderLevel: 5, isActive: true,
};
const CATEGORIES = ['Beverage', 'Snack', 'Alcohol', 'Tobacco', 'Other'];
const TAX_TYPES = ['GST', 'VAT'];

export default function MiniBarItemMaster() {
  const { companyId, hotelId, userId, currency } = ctx();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const [replOpen, setReplOpen] = useState(false);
  const [replForm, setReplForm] = useState({ miniBarItemId: 0, quantity: 1, reason: '' });
  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const r = await miniBarApi.listItems(hotelId, { page: page + 1, pageSize: rowsPerPage, search });
      setItems(r.data.items || []); setTotal(r.data.totalCount || 0);
    } catch { notify('Load failed', 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, rowsPerPage]);

  const onSave = async () => {
    if (!form.productName) return notify('Name required', 'warning');
    if (!form.sku) return notify('SKU required', 'warning');
    try {
      await miniBarApi.saveItem({ ...form, companyId, hotelId }, userId);
      notify('Saved'); setOpen(false); setForm(EMPTY); load();
    } catch { notify('Save failed', 'error'); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await miniBarApi.deleteItem(id, userId); notify('Deleted'); load(); }
    catch { notify('Failed', 'error'); }
  };

  const onReplenish = async () => {
    if (!replForm.quantity) return notify('Qty?', 'warning');
    try {
      await miniBarApi.replenish({ ...replForm, createdBy: userId });
      notify('Stock replenished'); setReplOpen(false); load();
    } catch { notify('Failed', 'error'); }
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Card>
        <CardContent>
          <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
            <Box display='flex' alignItems='center' gap={1}>
              <InventoryIcon color='primary' />
              <Typography variant='h5'>Mini-Bar Item Master</Typography>
            </Box>
            <Box display='flex' gap={1}>
              <Button variant='outlined' startIcon={<AddBusinessIcon />}
                onClick={() => { setReplForm({ miniBarItemId: 0, quantity: 1, reason: '' }); setReplOpen(true); }}>
                Replenish
              </Button>
              <Button variant='contained' startIcon={<AddIcon />}
                onClick={() => { setForm({ ...EMPTY, companyId, hotelId }); setOpen(true); }}>
                Add Item
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size='small' label='Search by name/SKU/category' value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()} />
            </Grid>
          </Grid>

          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell><TableCell>SKU</TableCell>
                  <TableCell>Category</TableCell><TableCell align='right'>Price</TableCell>
                  <TableCell>Tax Type</TableCell>
                  <TableCell align='right'>Rate %</TableCell><TableCell>HSN Code</TableCell>
                  <TableCell align='right'>Stock</TableCell>
                  <TableCell align='right'>Reorder</TableCell><TableCell>Status</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (<TableRow><TableCell colSpan={11} align='center'><CircularProgress size={24} /></TableCell></TableRow>)}
                {!loading && items.length === 0 && (<TableRow><TableCell colSpan={11} align='center'>No items</TableCell></TableRow>)}
                {items.map((x) => {
                  const low = x.stockQuantity <= x.reorderLevel;
                  return (
                    <TableRow key={x.miniBarItemId} hover>
                      <TableCell>{x.productName}</TableCell>
                      <TableCell>{x.sku}</TableCell>
                      <TableCell>{x.category}</TableCell>
                      <TableCell align='right'>{currency} {Number(x.price).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip size='small'
                          label={x.taxType || 'GST'}
                          color={x.taxType === 'VAT' ? 'warning' : 'primary'}
                          variant='outlined' />
                      </TableCell>
                      <TableCell align='right'>{x.gstPercent}</TableCell>
                      <TableCell>{x.hsnCode || '-'}</TableCell>
                      <TableCell align='right'>
                        {low && <Tooltip title='Low stock'><WarningAmberIcon fontSize='small' color='warning' /></Tooltip>}
                        <strong style={{ color: low ? '#c62828' : '#2e7d32' }}> {x.stockQuantity}</strong>
                      </TableCell>
                      <TableCell align='right'>{x.reorderLevel}</TableCell>
                      <TableCell><Chip size='small' label={x.isActive ? 'Active' : 'Inactive'}
                        color={x.isActive ? 'success' : 'default'} /></TableCell>
                      <TableCell align='right'>
                        <IconButton size='small' onClick={() => { setForm({ ...x }); setOpen(true); }}><EditIcon /></IconButton>
                        <IconButton size='small' color='error' onClick={() => onDelete(x.miniBarItemId)}><DeleteIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component='div' count={total} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} />
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>{form.miniBarItemId ? 'Edit' : 'Add'} Mini-Bar Item</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label='Product Name' value={form.productName}
              onChange={(e) => setForm({ ...form, productName: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label='SKU' value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label='Category' value={form.category || ''}
                onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type='number' label='Price' value={form.price}
              onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} /></Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth label='Tax Type' value={form.taxType || 'GST'}
                helperText='VAT for alcohol etc (no CGST/SGST split)'
                onChange={(e) => setForm({ ...form, taxType: e.target.value })}>
                {TAX_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type='number'
              label={`${form.taxType === 'VAT' ? 'VAT' : 'GST'} %`} value={form.gstPercent}
              onChange={(e) => setForm({ ...form, gstPercent: parseFloat(e.target.value) || 0 })} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label='HSN Code' value={form.hsnCode || ''}
              onChange={(e) => setForm({ ...form, hsnCode: e.target.value })} /></Grid>
            <Grid item xs={6} sm={6}><TextField fullWidth type='number' label='Opening Stock'
              disabled={form.miniBarItemId !== 0} value={form.stockQuantity}
              onChange={(e) => setForm({ ...form, stockQuantity: parseInt(e.target.value, 10) || 0 })} /></Grid>
            <Grid item xs={6} sm={6}><TextField fullWidth type='number' label='Reorder Level' value={form.reorderLevel}
              onChange={(e) => setForm({ ...form, reorderLevel: parseInt(e.target.value, 10) || 0 })} /></Grid>
            <Grid item xs={12}><FormControlLabel control={
              <Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
            } label='Active' /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={onSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={replOpen} onClose={() => setReplOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Replenish Stock</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth label='Item' value={replForm.miniBarItemId}
                onChange={(e) => setReplForm({ ...replForm, miniBarItemId: parseInt(e.target.value, 10) })}>
                <MenuItem value={0}>--Select--</MenuItem>
                {items.map((i) => (
                  <MenuItem key={i.miniBarItemId} value={i.miniBarItemId}>
                    {i.productName} (stock: {i.stockQuantity})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth type='number' label='Quantity' value={replForm.quantity}
              onChange={(e) => setReplForm({ ...replForm, quantity: parseInt(e.target.value, 10) || 0 })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label='Reason / PO No' value={replForm.reason}
              onChange={(e) => setReplForm({ ...replForm, reason: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={onReplenish}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
